/**
 * AI・IT用語辞典の第一階層。
 *
 * 現在のDBには旧カテゴリが1つだけ入っているため、URLを変えずに新しい
 * 情報設計へ移れるよう、このファイルで表示上の領域を決定する。将来DBへ
 * topicSlugを追加したときも、同じslugを使えばカテゴリURLは維持できる。
 */
export const GLOSSARY_TOPIC_SLUGS = [
  "ai",
  "programming",
  "web",
  "frontend",
  "backend",
  "database",
  "infrastructure",
  "security",
  "development-tools",
  "it-basics",
] as const;

// 最終目標の個別用語10,000語を1回のキャッシュ済み索引で扱う上限。
export const GLOSSARY_INDEX_LIMIT = 10_000;

export type GlossaryTopicSlug = (typeof GLOSSARY_TOPIC_SLUGS)[number];

export type GlossaryTopic = {
  slug: GlossaryTopicSlug;
  label: string;
  shortLabel: string;
  description: string;
  seoTitle: string;
  seoDescription: string;
  subtopics: readonly string[];
  /** トップと領域ページで、実在する用語だけを学習順に並べる候補。 */
  learningPathSlugs: readonly string[];
};

export const GLOSSARY_TOPICS: readonly GlossaryTopic[] = [
  {
    slug: "ai",
    label: "AI・生成AI",
    shortLabel: "AI",
    description:
      "生成AI、機械学習、LLM、RAGなど、いま知りたいAIの仕組みを図解で理解します。",
    seoTitle: "AI・生成AI用語集｜LLM・RAG・機械学習を図解で解説",
    seoDescription:
      "AI・生成AIの用語を初心者向けに図解で解説。機械学習、LLM、Transformer、RAG、AIエージェントなどを学ぶ順番でも探せます。",
    subtopics: ["生成AI", "機械学習", "LLM", "AI開発"],
    learningPathSlugs: [
      "machine-learning",
      "neural-network",
      "deep-learning",
      "generative-ai",
      "llm",
      "transformer",
      "token",
      "prompt",
      "embedding",
      "rag",
      "ai-agent",
      "mcp",
    ],
  },
  {
    slug: "programming",
    label: "プログラミング",
    shortLabel: "プログラミング",
    description:
      "変数、関数、データ型、言語の文法など、コードを書くための共通基礎を学びます。",
    seoTitle: "プログラミング用語集｜初心者向けに図解でわかりやすく解説",
    seoDescription:
      "プログラミング初心者が覚えたい用語を図解で解説。変数、関数、配列、クラス、フレームワークなどを順番に理解できます。",
    subtopics: [
      "基本文法",
      "データ型",
      "オブジェクト指向",
      "言語・フレームワーク",
    ],
    learningPathSlugs: [
      "program",
      "variable",
      "data-type",
      "conditional",
      "loop",
      "function",
      "array",
      "object",
      "class",
      "framework",
    ],
  },
  {
    slug: "web",
    label: "Web・インターネット",
    shortLabel: "Web",
    description:
      "HTTP、URL、API、ブラウザなど、Webがつながり表示される仕組みを学びます。",
    seoTitle: "Web・インターネット用語集｜HTTP・APIを図解で解説",
    seoDescription:
      "Web・インターネットの用語を初心者向けに図解で解説。HTTP、URL、API、Cookie、ブラウザの仕組みを体系的に学べます。",
    subtopics: ["Webの仕組み", "HTTP", "API", "ブラウザ"],
    learningPathSlugs: [
      "internet",
      "url",
      "http",
      "browser",
      "api",
      "rest-api",
      "json",
      "cookie",
      "cors",
    ],
  },
  {
    slug: "frontend",
    label: "フロントエンド",
    shortLabel: "フロントエンド",
    description:
      "HTML、CSS、JavaScript、Reactなど、ユーザーが触れる画面を作る技術を学びます。",
    seoTitle: "フロントエンド用語集｜HTML・CSS・JavaScriptを図解で解説",
    seoDescription:
      "フロントエンド開発の用語を図解で解説。HTML、CSS、JavaScript、TypeScript、ReactやDOMの基礎を探せます。",
    subtopics: ["HTML", "CSS", "JavaScript", "UI・フレームワーク"],
    learningPathSlugs: [
      "html",
      "semantic-html",
      "css",
      "responsive-design",
      "javascript",
      "dom",
      "typescript",
      "component",
      "spa",
    ],
  },
  {
    slug: "backend",
    label: "バックエンド",
    shortLabel: "バックエンド",
    description:
      "サーバー、Node.js、Java、認証処理など、サービスの裏側を動かす技術を学びます。",
    seoTitle: "バックエンド用語集｜サーバー・Node.js・Javaを図解で解説",
    seoDescription:
      "バックエンド開発の用語を初心者向けに図解で解説。サーバー、Node.js、Java、API実装の基礎を体系的に探せます。",
    subtopics: ["サーバーサイド", "Node.js", "Java", "アプリケーション設計"],
    learningPathSlugs: [
      "backend",
      "server",
      "nodejs",
      "express",
      "controller",
      "service",
      "session",
    ],
  },
  {
    slug: "database",
    label: "データベース",
    shortLabel: "DB",
    description:
      "SQL、テーブル、JOIN、インデックスなど、データを安全に扱う仕組みを学びます。",
    seoTitle: "データベース用語集｜SQL・JOIN・Indexを図解で解説",
    seoDescription:
      "データベース用語を初心者向けに図解で解説。SQL、テーブル、JOIN、インデックス、トランザクションを学ぶ順番でも探せます。",
    subtopics: ["SQL", "設計", "検索・集計", "運用"],
    learningPathSlugs: [
      "database",
      "sql",
      "table",
      "query",
      "join",
      "index-db",
      "transaction",
      "acid",
    ],
  },
  {
    slug: "infrastructure",
    label: "クラウド・インフラ",
    shortLabel: "インフラ",
    description:
      "Linux、Docker、AWS、ネットワークなど、サービスを動かし続ける基盤を学びます。",
    seoTitle: "クラウド・インフラ用語集｜Linux・Docker・AWSを図解で解説",
    seoDescription:
      "クラウド・インフラの用語を図解で解説。Linux、Docker、AWS、ネットワーク、デプロイの基礎を初心者向けにまとめています。",
    subtopics: ["Linux", "コンテナ", "クラウド", "ネットワーク"],
    learningPathSlugs: [
      "server",
      "linux",
      "container",
      "docker",
      "cloud",
      "aws",
      "cdn",
      "deploy",
    ],
  },
  {
    slug: "security",
    label: "セキュリティ・認証",
    shortLabel: "セキュリティ",
    description:
      "認証、OAuth、攻撃と対策など、Webサービスを安全に守る仕組みを学びます。",
    seoTitle: "セキュリティ・認証用語集｜OAuth・JWT・XSSを図解で解説",
    seoDescription:
      "ITセキュリティと認証の用語を初心者向けに図解で解説。OAuth、JWT、XSS、CSRF、パスワード管理の仕組みを学べます。",
    subtopics: ["認証・認可", "Web攻撃", "暗号・署名", "安全な実装"],
    learningPathSlugs: [
      "authentication",
      "authorization",
      "password",
      "session",
      "jwt",
      "oauth",
      "xss",
      "csrf",
      "csp",
    ],
  },
  {
    slug: "development-tools",
    label: "開発・Git・設計",
    shortLabel: "開発・Git",
    description:
      "Git、テスト、デバッグ、設計図など、チームで品質高く開発する方法を学びます。",
    seoTitle: "開発・Git・設計用語集｜GitHub・テスト・CI/CDを図解で解説",
    seoDescription:
      "ソフトウェア開発、Git、設計の用語を図解で解説。GitHub、ブランチ、テスト、デバッグ、CI/CD、UMLを体系的に探せます。",
    subtopics: ["Git・GitHub", "テスト", "設計", "チーム開発"],
    learningPathSlugs: [
      "git",
      "github",
      "branch",
      "commit",
      "code-review",
      "test",
      "debug",
      "ci-cd",
      "architecture",
    ],
  },
  {
    slug: "it-basics",
    label: "IT基礎・コンピュータ",
    shortLabel: "IT基礎",
    description:
      "OS、CPU、データ構造、アルゴリズムなど、ITを支える土台から理解します。",
    seoTitle: "IT基礎用語集｜OS・CPU・アルゴリズムを図解で解説",
    seoDescription:
      "ITとコンピュータの基礎用語を初心者向けに図解で解説。OS、CPU、メモリ、データ構造、アルゴリズムを体系的に学べます。",
    subtopics: ["コンピュータ", "OS", "データ構造", "アルゴリズム"],
    learningPathSlugs: [
      "bit",
      "cpu",
      "memory",
      "os",
      "data-structure",
      "stack",
      "queue",
      "tree",
      "algorithm",
    ],
  },
] as const;

const TOPIC_BY_SLUG = new Map(
  GLOSSARY_TOPICS.map((topic) => [topic.slug, topic]),
);

export type GlossaryTopicSource = {
  slug: string;
  term: string;
  category?: string | null;
  topicSlug?: string | null;
};

/**
 * 語の slug で話題を決める表。DB の topicSlug が空の語のうち、旧カテゴリや
 * 下の正規表現では取り違えるものをここで正す。
 * 旧カテゴリ「AI」には async/await や Fragment が、旧カテゴリ「プログラミング基礎」には
 * 機械学習の語や用語が混ざっており、カテゴリ名だけでは話題を決められない。
 * 優先順位は DB の topicSlug、この表、旧カテゴリ、正規表現の順。
 */
const SLUG_TOPIC_OVERRIDES: ReadonlyMap<string, GlossaryTopicSlug> = new Map(
  Object.entries({
  // ai
  "regression": "ai",
  "overfitting": "ai",
  "cross-validation": "ai",
  "supervised-learning": "ai",
  "unsupervised-learning": "ai",
  "forward-propagation": "ai",
  "back-propagation": "ai",
  "convolutional-layer": "ai",
  "evaluation": "ai",
  "llamaindex": "ai",
  // frontend
  "side-effect": "frontend",
  "promise-all": "frontend",
  "json-stringify": "frontend",
  "usefetch": "frontend",
  "classlist": "frontend",
  "classlist-toggle": "frontend",
  "media-query": "frontend",
  "form": "frontend",
  "position": "frontend",
  "fragment": "frontend",
  "localstorage": "frontend",
  "semantic-tag": "frontend",
  "border-radius": "frontend",
  "pseudo-class": "frontend",
  "auto-fill": "frontend",
  "mobile-first": "frontend",
  "hover": "frontend",
  "box-model": "frontend",
  "micro-interaction": "frontend",
  "alignment": "frontend",
  "contrast": "frontend",
  "sidebar": "frontend",
  "loading": "frontend",
  "conditional-rendering": "frontend",
  "one-way-data-flow": "frontend",
  "dependency-array": "frontend",
  "hot-reload": "frontend",
  "router": "frontend",
  "rendering": "frontend",
  "classname": "frontend",
  "frontend": "frontend",
  // backend
  "request-scope": "backend",
  "session-invalidate": "backend",
  "session-setattribute": "backend",
  "web-inf": "backend",
  "web-xml": "backend",
  "jsonify": "backend",
  "express-js": "backend",
  "middleware": "backend",
  "pom-xml": "backend",
  "session-scope": "backend",
  // web
  "http-method": "web",
  "query-parameter": "web",
  "redirect": "web",
  "post-redirect-get": "web",
  // programming
  "shared-types": "programming",
  "type-safety": "programming",
  "type-inference": "programming",
  "requirements-txt": "programming",
  "np-linspace": "programming",
  "csv-parse": "programming",
  "camel-case": "programming",
  "callback": "programming",
  "immutable-update": "programming",
  "immutable": "programming",
  "mutable": "programming",
  "indexerror": "programming",
  "encapsulation": "programming",
  "fullstack": "programming",
  "hashmap": "programming",
  "hashset": "programming",
  "async-await": "programming",
  "entry-point": "programming",
  // database
  "stock-deduction": "database",
  "functional-dependency": "database",
  "commit-db": "database",
  "record-db": "database",
  "read-replica": "database",
  // infrastructure
  "policy": "infrastructure",
  "bucket-policy": "infrastructure",
  "port-80": "infrastructure",
  "port-number": "infrastructure",
  "hosted-zone": "infrastructure",
  "storage": "infrastructure",
  "bastion-host": "infrastructure",
  "shared-responsibility-model": "infrastructure",
  "pay-as-you-go": "infrastructure",
  "multi-stage": "infrastructure",
  "cd": "infrastructure",
  "cache": "infrastructure",
  "latency": "infrastructure",
  "directory": "infrastructure",
  // security
  "ssl-tls": "security",
  "least-privilege": "security",
  // development-tools
  "pull-request": "development-tools",
  "coverage": "development-tools",
  "working-tree": "development-tools",
  "process-flow": "development-tools",
  "decision": "development-tools",
  "terminator": "development-tools",
  "activation-bar": "development-tools",
  "actor": "development-tools",
  "multiplicity": "development-tools",
  "review": "development-tools",
  "stage": "development-tools",
  "slack": "development-tools",
  "delivery": "development-tools",
  "operations-maintenance": "development-tools",
  "non-functional-requirement": "development-tools",
  "layer": "development-tools",
  "separation-of-concerns": "development-tools",
  "dependency-direction": "development-tools",
  // it-basics
  "hash-table": "it-basics",
  "process": "it-basics",
  "storage-device": "it-basics",
  "input-device": "it-basics",
  "output-device": "it-basics",
  "alu": "it-basics",
  "control-unit": "it-basics",
  "linked-list": "it-basics",
  "list-ds": "it-basics",
  "set-ds": "it-basics",
  "heap": "it-basics",
  "code-point": "it-basics",
  "character-encoding": "it-basics",
  "mojibake": "it-basics",
} as Record<string, GlossaryTopicSlug>),
);

const AUTHORITATIVE_CATEGORY_TOPICS: Record<string, GlossaryTopicSlug> = {
  AI: "ai",
  データベース: "database",
  DB: "database",
  インフラ: "infrastructure",
  Linux: "infrastructure",
  認証: "security",
  アルゴリズム: "it-basics",
  フロント: "frontend",
  Git: "development-tools",
  設計: "development-tools",
  ビルド: "development-tools",
};

const TOPIC_PATTERNS: readonly [GlossaryTopicSlug, RegExp][] = [
  [
    "security",
    /(?:auth|authorization|oauth|jwt|csrf|xss|csp|clickjack|password|bcrypt|signature|encrypt|decrypt|session-timeout|salt|認証|認可|暗号|署名|脆弱性|攻撃)/,
  ],
  [
    "ai",
    /(?:^|[- ])ai(?:[- ]|$)|chatgpt|claude|gemini|llm|(?:^|[- ])rag(?:[- ]|$)|mcp|embedding|transformer|prompt|machine-learning|deep-learning|neural-network|generative-ai|fine-tuning|few-shot|zero-shot|context-length|cosine-similarity|vectorization|vector-db|ai-agent|agentic|tool-calling|function-calling|hallucination|knowledge-cutoff|multimodal|(?:^|[- ])token(?:[- ]|$)|(?:^|[- ])chunk(?:[- ]|$)|neuron|pooling-layer|decisiontree|llamaindex|人工知能|機械学習|深層学習|生成ai|ニューラル|ベクトル化/,
  ],
  [
    "database",
    /database|(?:^|[- ])sql(?:[- ]|$)|nosql|mysql|postgres|sqlite|table|query|join|orm|index-db|transaction|acid|connection-pool|prepared-statement|junction-table|derived-table|composite-index|aggregation|seed-data|データベース|テーブル|クエリ|正規化|主キー|外部キー|集約/,
  ],
  [
    "frontend",
    /(?:^|[- ])html(?:[- ]|$)|semantic-html|innerhtml|(?:^|[- ])css(?:[- ]|$)|box-sizing|flex-wrap|align-items|justify-content|max-width|selector|responsive-design|javascript|typescript|(?:^|[- ])dom(?:[- ]|$)|spa|react|vite|component|custom-hook|localstorage|frontend|フロントエンド/,
  ],
  [
    "backend",
    /backend|nodejs|node-js|express|app-use|req-params|servlet|jsp|jstl|tomcat|spring|controller|(?:^|[- ])service(?:[- ]|$)|(?:^|[- ])dao(?:[- ]|$)|(?:^|[- ])dto(?:[- ]|$)|httpd|httpsession|サーバーサイド|バックエンド/,
  ],
  [
    "development-tools",
    /(?:^|[- ])git(?:[- ]|$)|github|git-clone|git-pull|branch|rebase|merge|conflict|code-review|(?:^|[- ])test(?:[- ]|$)|software-testing|coverage|debug|breakpoint|ci-cd|(?:^|[- ])ci(?:[- ]|$)|build|dependencies|devdependencies|package-json|package-lock|tsconfig|architecture|class-diagram|system-diagram|swimlane|draw-io|agile|waterfall|raci|team-development|設計|テスト|デバッグ|ブランチ|チーム開発/,
  ],
  [
    "infrastructure",
    /aws|cloud|cloudfront|docker|dockerfile|container|kubernetes|linux|kernel|shell|terminal|system-call|deploy|staging|storage|health-check|cdn|network|tcp|udp|dns|cidr|firewall|ssh|chmod|systemctl|クラウド|インフラ|ネットワーク/,
  ],
  [
    "web",
    /(?:^|[- ])api(?:[- ]|$)|rest-api|fetch-api|(?:^|[- ])http(?:[- ]|$)|http-method|http-request|response-ok|status-code|url|cookie|cors|browser|internet|protocol|domain|query-parameter|websocket|streaming|web-inf|インターネット|ブラウザ|リクエスト|レスポンス/,
  ],
  [
    "it-basics",
    /algorithm|data-structure|hash-table|hash-function|stack|queue|(?:^|[- ])tree(?:[- ]|$)|graph|bit|binary|cpu|memory|storage-device|thread|process|operating-system|(?:^|[- ])os(?:[- ]|$)|system-call|complexity|sort|search|recursion|computer|データ構造|アルゴリズム|計算量|コンピュータ|記憶装置/,
  ],
];

export function getGlossaryTopicBySlug(
  slug: string | null | undefined,
): GlossaryTopic | undefined {
  if (!slug) return undefined;
  return TOPIC_BY_SLUG.get(slug as GlossaryTopicSlug);
}

export function getGlossaryTopic(source: GlossaryTopicSource): GlossaryTopic {
  const storedTopic = getGlossaryTopicBySlug(source.topicSlug);
  if (storedTopic) return storedTopic;

  const overridden = SLUG_TOPIC_OVERRIDES.get(source.slug);
  if (overridden) return TOPIC_BY_SLUG.get(overridden)!;

  const category = source.category?.trim() ?? "";
  const authoritative = AUTHORITATIVE_CATEGORY_TOPICS[category];
  if (authoritative) return TOPIC_BY_SLUG.get(authoritative)!;

  const searchable = `${source.slug} ${source.term}`.toLowerCase();
  for (const [topicSlug, pattern] of TOPIC_PATTERNS) {
    if (pattern.test(searchable)) return TOPIC_BY_SLUG.get(topicSlug)!;
  }

  if (category === "Web開発" || category === "Web") {
    return TOPIC_BY_SLUG.get("web")!;
  }
  if (category === "Java" || category === "Python") {
    return TOPIC_BY_SLUG.get("programming")!;
  }

  return TOPIC_BY_SLUG.get("programming")!;
}

export function getGlossaryTopicHref(topic: GlossaryTopic | GlossaryTopicSlug) {
  const slug = typeof topic === "string" ? topic : topic.slug;
  return `/glossary/topics/${slug}`;
}
